/**
 * The one error type that means "the sprite is wrong", not "we are broken".
 *
 * It lives alone because both halves of validation need to throw it and they
 * cannot import each other: spec.ts already imports kit.ts, so kit.ts importing
 * spec.ts would be a cycle.
 *
 * That is not a tidiness point. The Worker decides what to tell somebody by
 * asking whether the error is one of these, and kit.ts was throwing plain
 * Errors, so a malformed kit came back as "Bitwright could not reach the
 * model" when the truth was that the model had written a bad colour. It was
 * papered over by sniffing the message for the word "unknown", which worked
 * until a message started with a different word.
 */
/**
 * The model was asked for a subject the tool does not draw.
 *
 * Its own type because it is not a failure: nothing is broken, nothing should
 * be retried, and it must not be reported as one. The Worker turns it into a
 * plain refusal that costs the visitor nothing from their allowance, since they
 * received nothing. See the refusal instruction in src/vocabulary.ts.
 */
export class Refused extends Error {
	constructor() {
		super("refused");
		this.name = "Refused";
	}
}

export class SpecError extends Error {}

/**
 * Bitwright's own account at the model provider has stopped paying out.
 *
 * A monthly spend cap has been reached, or the balance is gone. Its own type
 * because it is the one failure that looks exactly like an outage from outside
 * and is not one: nothing is broken, retrying will not help, and it will clear
 * on its own when the month turns or somebody tops up.
 *
 * Without this the site could only say "something went wrong reaching the
 * service", which is the same sentence a real outage produces. That is the
 * difference between a person knowing to come back tomorrow and a person
 * concluding the tool is broken and not returning.
 *
 * Note whose money this is: the visitor's own credits are a separate thing
 * entirely and are checked long before the model is called. This is the shop
 * being out of stock, not the customer being out of money.
 */
export class SpendCapped extends Error {
	constructor() {
		super("spend capped");
		this.name = "SpendCapped";
	}
}

/**
 * The reply ran out of room before the spec was finished.
 *
 * Its own type because it looks like every other broken-JSON failure and is
 * not one: nothing is wrong with the tool or the model, the subject simply
 * needed more tokens than a reply is allowed. Retrying the same request
 * truncates at the same place, so this must not be repaired, and the person
 * needs telling that a simpler subject or a smaller canvas is the way through
 * rather than "try again shortly".
 *
 * It appeared the day the `art` shape shipped. A drawn sprite writes every
 * pixel out longhand, so a detailed character can want several times the
 * tokens of an assembled machine, and the ceiling had been sized for machines.
 */
export class Truncated extends Error {
	constructor() {
		super("the reply hit its length limit before the sprite was finished");
		this.name = "Truncated";
	}
}
